import * as React from 'react';
import snakeCase from 'lodash/snakeCase';

import PopoverList from 'components/popover-list/PopoverList';

import { __t, applyTranslations } from 'utils/language';
import { getIGStorySnippetTypeKey } from 'utils/reporting';

import './content-card-stats-row.scss';

import type {
  ContentCardContentType,
  ContentCardMetricType,
  ContentCardStatsType
} from 'components/cards/content-card/types';
import type {
  ReportingMetaInfoIGStorySnippetType
} from 'modules/reporting/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('components/cards/content-card/shared/content-card-shared.i18n.json'));

type Props = {
  type: ContentCardStatsType,
  value: string,
  contentType: ContentCardContentType,
  metricType: ContentCardMetricType,
  hidePopover?: boolean,
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType
};

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'content_card_shared';

const ContentCardStatsRow = ({
  type,
  value,
  contentType,
  metricType,
  hidePopover,
  igStorySnippetType
}: Props): JSX.Element => {
  let msgKeyAdditional = '';

  if (type.includes('engagement-rate')) {
    msgKeyAdditional = '_long';
  }

  const isVerbosePopover = contentType === 'story' && type === 'total-reach';
  const snippetKey = getIGStorySnippetTypeKey(igStorySnippetType);
  const label = __t(`${SHARED_MSG_KEY}.labels.${snakeCase(type)}`, {
    type: isVerbosePopover
      ? __t(`${SHARED_MSG_KEY}.labels.${snippetKey}`)
      : __t(`${SHARED_MSG_KEY}.labels.total`)
  });

  let title = __t(`${SHARED_MSG_KEY}.labels.${snakeCase(type)}${msgKeyAdditional}`, {
    type: isVerbosePopover
      ? __t(`${SHARED_MSG_KEY}.labels.${snippetKey}`)
      : __t(`${SHARED_MSG_KEY}.labels.total`)
  });

  let body = __t(`${SHARED_MSG_KEY}.tooltips.${snakeCase(type)}`, {
    contentType,
    metricType: (metricType || '').toLowerCase(),
    additional: __t(`${SHARED_MSG_KEY}.tooltips.total_reactions_additional_${contentType}`)
  });

  if (isVerbosePopover) {
    title = __t(`${SHARED_MSG_KEY}.tooltips.verbose_descriptions.title`, {
      metric: __t(`${COMMON_MSG_KEY}.kpis.reach`),
      type: snippetKey,
      value
    });

    body = __t(`${SHARED_MSG_KEY}.tooltips.verbose_descriptions.${igStorySnippetType}`);
  }

  return (
    <div className="content-card-stats-row">
      <div
        className="content-card-stats-row__label"
        title={`${label}: ${value}`}
      >
        {label}
      </div>
      {
        !hidePopover && (
          <div className="content-card-stats-row__popover">
            <PopoverList
              key={`content-card-stats-popover--${title}-${body}`}
              list={[{
                title,
                body
              }]}
              width={isVerbosePopover ? 'wide' : 'short'}
              lineHeight={isVerbosePopover ? 'normal' : 'inherit'}
            />
          </div>
        )
      }
      <div
        className="content-card-stats-row__value"
        title={`${label}: ${value}`}
      >
        {value}
      </div>
    </div>
  );
};

export default ContentCardStatsRow;
